/**
 * AuthError.tsx
 *
 * Purpose: OAuth error page for user-friendly authentication failures.
 * Created: 2025-01-19
 * Modified: 2025-01-19
 * Contract: shared/contracts/auth/v1/oauth-error.yaml
 *
 * WHO: Users redirected after OAuth failures.
 * WHAT: Parses query params and displays contract-aligned error UI.
 * WHERE: Mythbound.App/src/pages/AuthError.tsx.
 * WHY: Provides clear recovery actions for failed OAuth flows.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Link as ChakraLink
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';
import { GlobalFooter } from '../components/ui/GlobalFooter';

type OAuthErrorCode = 'oauth_failed' | 'token_expired' | 'provider_error' | 'state_mismatch' | 'access_denied';

const ERROR_MESSAGES: Record<OAuthErrorCode, { title: string; description: string }> = {
  oauth_failed: {
    title: 'Authentication Failed',
    description: "We couldn't complete the sign-in process. This might be a temporary issue."
  },
  token_expired: {
    title: 'Session Expired',
    description: 'Your authentication session has expired. Please try signing in again.'
  },
  provider_error: {
    title: 'Provider Unavailable',
    description: 'The authentication provider is temporarily unavailable. Please try again later.'
  },
  state_mismatch: {
    title: 'Security Check Failed',
    description: "The authentication request couldn't be verified. Please try signing in again."
  },
  access_denied: {
    title: 'Access Denied',
    description: "You declined the permission request or your account doesn't have access."
  }
};

const PROVIDER_LABELS: Record<string, string> = {
  discord: 'Discord',
  google: 'Google',
  unknown: 'Unknown'
};

const sanitizeErrorMessage = (message: string | null): string | null => {
  if (!message) {
    return null;
  }
  const normalized = message.replace(/[\r\n]+/g, ' ').replace(/[<>]/g, '').trim();
  return normalized.slice(0, 500);
};

/**
 * AuthError Component
 *
 * WHO: OAuth users redirected after authentication errors.
 * WHAT: Renders a themed error UI with retry and navigation options.
 * WHERE: /auth/error route in the React application.
 * WHY: Provides a friendly, actionable error experience for failed OAuth flows.
 */
export const AuthError: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const retryButtonRef = useRef<HTMLButtonElement>(null);

  const { provider, message, errorInfo } = useMemo(() => {
    const rawError = searchParams.get('error') ?? 'oauth_failed';
    const rawProvider = searchParams.get('provider') ?? 'unknown';
    const rawMessage = searchParams.get('message');

    const safeError = Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, rawError)
      ? (rawError as OAuthErrorCode)
      : 'oauth_failed';
    const safeProvider = Object.prototype.hasOwnProperty.call(PROVIDER_LABELS, rawProvider)
      ? rawProvider
      : 'unknown';

    return {
      provider: safeProvider,
      message: sanitizeErrorMessage(rawMessage),
      errorInfo: ERROR_MESSAGES[safeError]
    };
  }, [searchParams]);

  useEffect(() => {
    retryButtonRef.current?.focus();
  }, []);

  const handleRetry = () => {
    if (provider !== 'unknown') {
      navigate(`/login?provider=${provider}`);
      return;
    }
    navigate('/login');
  };

  const handleHome = () => {
    navigate('/');
  };

  return (
    <>
      <Flex
        minHeight="100vh"
        width="full"
        align="center"
        justifyContent="center"
        bg={mythboundTheme.colors.gradients.darkPortal}
        px={4}
        py={12}
      >
        <Box
          maxW="520px"
          w="full"
          p={{ base: 6, md: 8 }}
          borderWidth={2}
          borderRadius="lg"
          boxShadow={mythboundTheme.shadows.glow}
          borderColor={mythboundTheme.colors.primary.main}
          bg="rgba(17, 17, 22, 0.9)"
        >
          <VStack spacing={6} align="stretch">
            <Flex justify="center">
              <Box
                width="72px"
                height="72px"
                borderRadius="full"
                bg="rgba(255, 186, 73, 0.18)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <WarningIcon boxSize={8} color="orange.300" aria-hidden="true" />
              </Box>
            </Flex>

            <Box textAlign="center" role="alert" aria-live="polite">
              <Heading
                fontFamily={mythboundTheme.typography.fontFamily.heading}
                color={mythboundTheme.colors.secondary.white}
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={2}
              >
                {errorInfo.title}
              </Heading>
              <Text color={mythboundTheme.colors.accent.silver}>
                {message || errorInfo.description}
              </Text>
            </Box>

            {provider !== 'unknown' && (
              <HStack justify="center">
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="rgba(255, 255, 255, 0.08)"
                  color={mythboundTheme.colors.secondary.white}
                  textTransform="capitalize"
                >
                  via {PROVIDER_LABELS[provider] ?? 'Unknown'}
                </Badge>
              </HStack>
            )}

            <VStack spacing={3}>
              <Button
                ref={retryButtonRef}
                width="full"
                onClick={handleRetry}
                bg={mythboundTheme.colors.primary.main}
                color={mythboundTheme.colors.secondary.white}
                _hover={{ bg: 'rgba(59, 232, 255, 0.85)' }}
              >
                Try Again
              </Button>
              <Button
                width="full"
                variant="outline"
                borderColor={mythboundTheme.colors.accent.silver}
                color={mythboundTheme.colors.accent.silver}
                _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                onClick={handleHome}
              >
                Return Home
              </Button>
            </VStack>

            <Box textAlign="center">
              <ChakraLink
                as={RouterLink}
                to="/help/auth-troubleshooting"
                color={mythboundTheme.colors.primary.main}
                textDecoration="underline"
                fontSize="sm"
              >
                Need help with authentication?
              </ChakraLink>
            </Box>
          </VStack>
        </Box>
      </Flex>
      <Box px={{ base: 4, md: 8 }} pb={6}>
        <GlobalFooter />
      </Box>
    </>
  );
};

export default AuthError;
